// What a browser may tell the server about ads — the shared, isomorphic half.
//
// Two facts ride the first-touch POST (lib/utm.ts → app/api/attribution) beside
// the first touch itself, and both are the visitor's to edit, so both are
// narrowed here on the way out and again on the way in:
//
//   · `x_click` — X's own click id (`?twclid=`), present when the visit came off
//     an X ad. LAST click, not first touch: an organic visit weeks ago must not
//     hide the ad click that brought them back to sign up. Kept 30 days.
//   · `ads_opt_out` — the person said "do not share" (the switch on /privacy),
//     or their browser sends Global Privacy Control. Either one means nothing
//     about this account goes to X.
//
// No cookies, as everywhere in attribution: the click id lives in localStorage.

/** The body fields the first-touch POST carries for ads. */
export interface AdsSignal {
  x_click?: string;
  ads_opt_out?: boolean;
}

/** X documents no format beyond "parse it from the twclid parameter"; ours is
 *  url-safe and bounded so a hand-edited store cannot use us as storage. Short
 *  ids pass: Sharuya's end-to-end test lands with `?twclid=test123`. */
const TWCLID = /^[A-Za-z0-9_.~-]{1,256}$/;

export function sanitizeTwclid(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const s = raw.trim();
  return TWCLID.test(s) ? s : undefined;
}

/** A click id older than this is no longer the reason someone signed up. */
export const X_CLICK_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Narrow a POST body's ads fields. Unknown or malformed values are dropped. */
export function readAdsSignal(body: unknown): AdsSignal {
  const src = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const out: AdsSignal = {};
  const click = sanitizeTwclid(src.x_click);
  if (click) out.x_click = click;
  if (src.ads_opt_out === true) out.ads_opt_out = true;
  return out;
}
